import { Temporal } from '@js-temporal/polyfill';
/**
 * Client-side logger that tunnels logs to the server via a pluggable transport.
 * Designed for browser environments.
 */

/**
 * Client log entry sent to the server.
 */
export interface ClientLogEntry {
  timestamp: string;
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
  data?: Record<string, unknown> | undefined;
  metadata: {
    url: string;
    userAgent?: string | undefined;
  };
}

/**
 * Transport interface for sending log entries to the server.
 *
 * Transports may signal that a failure is permanent (the entries should be
 * dropped, not re-queued) by rejecting with an error whose `permanent`
 * property is `true` — typically a `PermanentTransportError`.
 */
export interface ClientLogTransport {
  send(entries: readonly ClientLogEntry[]): Promise<void>;
  connect?(): void;
  disconnect?(): void;
}

/**
 * Error type that signals to `ClientLogger` that a batch should be dropped
 * rather than re-queued, and that the failure should not escalate the
 * flush backoff. Used by `HttpTransport` for non-retryable HTTP responses
 * (4xx other than 408 and 429).
 */
export class PermanentTransportError extends Error {
  readonly permanent = true as const;
  readonly status: number | undefined;
  /** Construct with an error message and an optional HTTP status code for diagnostic purposes. */
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'PermanentTransportError';
    this.status = status;
  }
}

/** Returns true if the error signals a permanent (non-retryable) transport failure that should drop the batch. */
function isPermanentTransportError(error: unknown): boolean {
  return (
    error instanceof PermanentTransportError ||
    (typeof error === 'object' &&
      error != null &&
      (error as { permanent?: unknown }).permanent === true)
  );
}

interface BrowserWindow {
  location: { href: string };
}

/**
 * Configuration options for the client logger.
 */
export interface ClientLoggerOptions {
  /** Transport for sending logs to the server */
  transport: ClientLogTransport;
  /** Maximum queue size when disconnected (default: 100) */
  maxQueueSize?: number;
  /** Base flush interval in ms (default: 1000) */
  flushInterval?: number;
  /** Maximum backoff delay in ms when flushes are failing (default: 60_000) */
  maxBackoffMs?: number;
  /** Backoff jitter as a fraction of the computed delay (default: 0.2 → ±20%) */
  backoffJitter?: number;
  /** Whether to also log to browser console (default: true) */
  logToConsole?: boolean;
}

/**
 * Client logger that sends logs to the server via a pluggable transport.
 *
 * Flushes happen on a periodic timer. When the transport rejects a flush,
 * the entries are re-queued (subject to `maxQueueSize`) and the next flush
 * is delayed exponentially: `flushInterval * 2^failures`, capped at
 * `maxBackoffMs`, with ±`backoffJitter` jitter so multiple tabs don't sync.
 * On a successful flush — or a permanent (non-retryable) failure — the
 * failure counter resets and the loop returns to `flushInterval`.
 */
export class ClientLogger {
  private transport: ClientLogTransport;
  private queue: ClientLogEntry[] = [];
  private maxQueueSize: number;
  private flushInterval: number;
  private maxBackoffMs: number;
  private backoffJitter: number;
  private logToConsole: boolean;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private isConnected = false;
  private consecutiveFailures = 0;
  private isFlushing = false;

  /** Construct a logger with the given transport and tuning options; call `connect()` to start flushing. */
  constructor(options: ClientLoggerOptions) {
    this.transport = options.transport;
    this.maxQueueSize = options.maxQueueSize ?? 100;
    this.flushInterval = options.flushInterval ?? 1000;
    this.maxBackoffMs = options.maxBackoffMs ?? 60_000;
    this.backoffJitter = options.backoffJitter ?? 0.2;
    this.logToConsole = options.logToConsole ?? true;
  }

  /**
   * Start the logger (begins periodic flushing and connects the transport).
   */
  connect(): void {
    if (this.isConnected) return;
    this.transport.connect?.();
    this.isConnected = true;
    this.scheduleFlush();
  }

  /**
   * Stop the logger, flush remaining logs, and disconnect the transport.
   */
  disconnect(): void {
    if (!this.isConnected) return;
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.isConnected = false;
    void this.flush().finally(() => {
      this.transport.disconnect?.();
    });
  }

  /** Enqueue a log-level message for the next flush. */
  log(message: string, data?: Record<string, unknown>): void {
    this.sendLog('log', message, data);
  }

  /** Enqueue an info-level message for the next flush. */
  info(message: string, data?: Record<string, unknown>): void {
    this.sendLog('info', message, data);
  }

  /** Enqueue a warn-level message for the next flush. */
  warn(message: string, data?: Record<string, unknown>): void {
    this.sendLog('warn', message, data);
  }

  /** Enqueue an error-level message for the next flush. */
  error(message: string, data?: Record<string, unknown>): void {
    this.sendLog('error', message, data);
  }

  /** Enqueue a debug-level message for the next flush. */
  debug(message: string, data?: Record<string, unknown>): void {
    this.sendLog('debug', message, data);
  }

  /** Enqueue an entry at the given level, optionally mirror to console, and trigger an early flush when the queue is full and not in backoff. */
  private sendLog(
    level: ClientLogEntry['level'],
    message: string,
    data?: Record<string, unknown>
  ): void {
    if (this.logToConsole) {
      const consoleMethod = level === 'log' ? 'log' : level;
      console[consoleMethod](`[${level.toUpperCase()}]`, message, data ?? '');
    }

    const browserWindow = (globalThis as Record<string, unknown>)['window'] as
      | BrowserWindow
      | undefined;
    const url: string = browserWindow?.location.href ?? '';
    const userAgent = typeof navigator === 'undefined' ? undefined : navigator.userAgent;

    const entry: ClientLogEntry = {
      timestamp: Temporal.Now.instant().toString(),
      level,
      message,
      data: this.sanitizeData(data),
      metadata: { url, userAgent },
    };

    this.queue.push(entry);
    if (this.queue.length > this.maxQueueSize) {
      this.queue.shift();
    }

    // Flush immediately when the queue is full, but only when we're not
    // already flushing and not in backoff. During backoff, the scheduled
    // timer is the single point of truth for flush cadence — if we let
    // every queue-full push trigger an ad-hoc flush, sustained logging
    // would defeat the backoff entirely.
    if (
      this.isConnected &&
      this.queue.length >= this.maxQueueSize &&
      this.consecutiveFailures === 0 &&
      !this.isFlushing
    ) {
      this.runFlushCycle();
    }
  }

  /** Cancel any pending timer and immediately begin a flush, rescheduling after completion. */
  private runFlushCycle(): void {
    if (this.isFlushing) return;
    if (this.flushTimer != null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    void this.flushAndReschedule();
  }

  /** Flush the queue and reschedule the next flush timer, setting `isFlushing` to serialize concurrent callers. */
  private async flushAndReschedule(): Promise<void> {
    this.isFlushing = true;
    try {
      await this.flush();
    } finally {
      this.isFlushing = false;
      if (this.isConnected) {
        this.scheduleFlush();
      }
    }
  }

  /** Arm the next flush timer using the backoff-adjusted delay, guarded against double-arming. */
  private scheduleFlush(): void {
    if (!this.isConnected || this.flushTimer) return;
    const delay = this.computeNextDelay();
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.runFlushCycle();
    }, delay);
  }

  /** Compute the next flush delay, applying exponential backoff with jitter when previous flushes have failed. */
  private computeNextDelay(): number {
    if (this.consecutiveFailures === 0) return this.flushInterval;
    // Cap exponent at 31 to avoid Math.pow overflow on pathological counters;
    // the maxBackoffMs cap below kicks in long before this matters.
    const exp = Math.min(this.consecutiveFailures, 31);
    const escalated = this.flushInterval * 2 ** exp;
    const jitterFactor = 1 + this.backoffJitter * (Math.random() * 2 - 1);
    // Apply jitter to the escalated delay first, then clamp — applying the
    // cap before jitter would let positive jitter push the delay above
    // maxBackoffMs (e.g., +20% on a 60s cap → 72s).
    const jittered = escalated * jitterFactor;
    return Math.max(0, Math.min(this.maxBackoffMs, jittered));
  }

  /** Drain the queue by sending all pending entries via the transport, re-queuing on transient failures and dropping on permanent ones. */
  private async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    const entries = this.queue;
    this.queue = [];
    try {
      await this.transport.send(entries);
      this.consecutiveFailures = 0;
    } catch (error) {
      if (isPermanentTransportError(error)) {
        // Drop the entries — they will never succeed. Reset the failure
        // counter so we don't keep backing off on what is, from the
        // server's point of view, a healthy exchange.
        this.consecutiveFailures = 0;
        return;
      }
      this.consecutiveFailures += 1;
      const merged = [...entries, ...this.queue];
      this.queue =
        merged.length > this.maxQueueSize
          ? merged.slice(merged.length - this.maxQueueSize)
          : merged;
    }
  }

  /** Deep-clone the metadata payload via `structuredClone` so mutations after enqueue don't corrupt the log entry; falls back to a sentinel on unserializable values. */
  private sanitizeData(data?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (data == null) return undefined;
    try {
      return structuredClone(data);
    } catch {
      return { __unserializable: true };
    }
  }
}
